import toast from "react-hot-toast";
import { currentDictionary, format } from "../i18n";
import { endSession } from "./auth-client";

/** One toast id for every throttled response, so a page firing ten queries shows one message. */
const RATE_LIMIT_TOAST_ID = "rate-limited";

/** Below this, "wait a moment" says more than a number would. */
const SECONDS_WORTH_NAMING = 90;

/**
 * Apollo throws on any status at or above 300 before it reads the body, so the message the API
 * sends with a 429 never reaches a component — the UI sees only "Received status code 429".
 * Surface it here instead, where the response is still intact.
 */
function reportRateLimited(response: Response): void {
  const t = currentDictionary();
  const seconds = Number.parseInt(
    response.headers.get("retry-after") ?? response.headers.get("ratelimit-reset") ?? "",
    10
  );

  const message =
    Number.isFinite(seconds) && seconds > SECONDS_WORTH_NAMING
      ? format(t["error.rateLimitedMinutes"], { minutes: Math.ceil(seconds / 60) })
      : t["error.rateLimited"];

  toast.error(message, { id: RATE_LIMIT_TOAST_ID });
}

/** One id per reason, so repeated queries under one failure show one message each. */
const ACCESS_LOCKED_TOAST_ID = "access-locked";

/**
 * Hands every response back as it came, bar three: signing in elsewhere invalidates this session
 * server-side, and the API says so on every request afterwards — end the session on that answer
 * rather than leaving a teacher on a screen whose Try again can never work. A throttled request
 * gets a message, because Apollo would otherwise swallow the one the API sent.
 *
 * The body is only ever read from a clone, so what the caller parses is untouched.
 */
export async function sessionCheckedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status === 429) {
    reportRateLimited(response);
    return response;
  }

  if (response.status !== 401 && response.status !== 403) return response;

  const body = await response
    .clone()
    .json()
    .catch(() => null);

  if (body?.code === "LOGGED_IN_ELSEWHERE") await endSession();

  // A suspended account would otherwise read as an unexplained failure: Apollo discards the body
  // above 300, so the reason the API gives never reaches a component. Not signed out — the lock
  // usually expires, and signing in again would succeed and then fail on every request.
  if (body?.code === "ACCESS_LOCKED") {
    toast.error(currentDictionary()["error.accessLocked"], { id: ACCESS_LOCKED_TOAST_ID });
  }

  return response;
}
